import { useCallback, useEffect, useState } from "react";

import {
  type CreateRepositoryChatConversationBody,
  type RepositoryChatConversation,
  createANewRepositoryChatConversationAsUser,
  getARepositoryChatConversationAsUser,
  listRepositoryChatConversationsAsUser,
} from "../dashboard-client/index";

interface ConversationState {
  conversation: RepositoryChatConversation | null;
  loading: boolean;
}

interface ConversationsState {
  items: RepositoryChatConversation[];
  loading: boolean;
}

export interface RepositoryChatWorkspaceState {
  conversations: RepositoryChatConversation[];
  creatingConversation: boolean;
  error: string | null;
  loadingConversation: boolean;
  loadingConversations: boolean;
  refreshConversations: () => Promise<void>;
  refreshSelectedConversation: () => Promise<void>;
  selectedConversation: RepositoryChatConversation | null;
  selectedConversationId: string | null;
  selectConversation: (conversationId: string) => void;
  startConversation: (
    input: CreateRepositoryChatConversationBody,
  ) => Promise<RepositoryChatConversation | null>;
}

export function useRepositoryChatWorkspace(
  selectedConversationId: null | string,
  onSelectConversation: (conversationId: null | string) => void,
): RepositoryChatWorkspaceState {
  const [conversationsState, setConversationsState] =
    useState<ConversationsState>({ items: [], loading: true });
  const [conversationState, setConversationState] = useState<ConversationState>(
    { conversation: null, loading: false },
  );
  const [creatingConversation, setCreatingConversation] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadConversations = useCallback(async () => {
    setConversationsState((current) => ({ ...current, loading: true }));
    setError(null);

    try {
      const { data } = await listRepositoryChatConversationsAsUser();
      setConversationsState({ items: data.items, loading: false });
      onSelectConversation(
        resolveSelectedConversationId(selectedConversationId, data.items),
      );
    } catch (loadError) {
      setConversationsState({ items: [], loading: false });
      setConversationState({ conversation: null, loading: false });
      onSelectConversation(null);
      setError(getErrorMessage(loadError, "Failed to load repository chats."));
    }
  }, [onSelectConversation, selectedConversationId]);

  const loadConversation = useCallback(
    async (conversationId: string) => {
      setConversationState((current) => ({ ...current, loading: true }));
      setError(null);

      try {
        const { data } = await getARepositoryChatConversationAsUser({
          path: { conversationId },
        });
        setConversationState({ conversation: data, loading: false });
        setConversationsState((current) => ({
          items: upsertConversation(current.items, data),
          loading: current.loading,
        }));
      } catch (loadError) {
        setConversationState({ conversation: null, loading: false });
        if (isRepositoryChatNotFoundError(loadError)) {
          onSelectConversation(null);
          void loadConversations();
          return;
        }
        setError(
          getErrorMessage(loadError, "Failed to load the conversation."),
        );
      }
    },
    [loadConversations, onSelectConversation],
  );

  const refreshSelectedConversation = useCallback(async () => {
    if (!selectedConversationId) {
      return;
    }

    await loadConversation(selectedConversationId);
  }, [loadConversation, selectedConversationId]);

  const startConversation = useCallback(
    async (input: CreateRepositoryChatConversationBody) => {
      setCreatingConversation(true);
      setError(null);

      try {
        const { data } = await createANewRepositoryChatConversationAsUser({
          body: input,
        });
        setConversationsState((current) => ({
          items: [
            data,
            ...current.items.filter(
              (item) => item.conversationId !== data.conversationId,
            ),
          ],
          loading: current.loading,
        }));
        setConversationState({ conversation: data, loading: false });
        onSelectConversation(data.conversationId);
        return data;
      } catch (createError) {
        setError(
          getErrorMessage(
            createError,
            "Failed to start a new repository chat.",
          ),
        );
        return null;
      } finally {
        setCreatingConversation(false);
      }
    },
    [onSelectConversation],
  );

  useEffect(() => {
    void loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    if (selectedConversationId || conversationsState.items.length === 0) {
      return;
    }

    onSelectConversation(conversationsState.items[0]?.conversationId ?? null);
  }, [conversationsState.items, onSelectConversation, selectedConversationId]);

  useEffect(() => {
    if (!selectedConversationId) {
      setConversationState({ conversation: null, loading: false });
      return;
    }

    void loadConversation(selectedConversationId);
  }, [loadConversation, selectedConversationId]);

  return {
    conversations: conversationsState.items,
    creatingConversation,
    error,
    loadingConversation: conversationState.loading,
    loadingConversations: conversationsState.loading,
    refreshConversations: loadConversations,
    refreshSelectedConversation,
    selectConversation: onSelectConversation,
    selectedConversation: conversationState.conversation,
    selectedConversationId,
    startConversation,
  };
}

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function resolveSelectedConversationId(
  current: null | string,
  conversations: RepositoryChatConversation[],
): null | string {
  if (
    current &&
    conversations.some((item) => item.conversationId === current)
  ) {
    return current;
  }

  return conversations[0]?.conversationId ?? null;
}

function isRepositoryChatNotFoundError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "error" in error &&
    error.error === "not_found"
  );
}

function upsertConversation(
  conversations: RepositoryChatConversation[],
  conversation: RepositoryChatConversation,
): RepositoryChatConversation[] {
  const next = conversations.filter(
    (item) => item.conversationId !== conversation.conversationId,
  );
  next.unshift(conversation);
  return next;
}

export const __testing = {
  isRepositoryChatNotFoundError,
  resolveSelectedConversationId,
};
